import { useState } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { backofficeAppConfig } from '@/backoffice/app/config/backoffice-app-config';
import { ProtectedRoute } from '@/backoffice/app/protected-route/protected-route';
import { legacyConsoleLatencyRoutes } from '@/backoffice/app/routes/console-latency/routes';
import { dataExportRoutes } from '@/backoffice/app/routes/data-exports/routes';
import { ec2OrchestratorConfigRoutes } from '@/backoffice/app/routes/ec2-orchestrator-config/routes';
import { BackofficeErrorBoundaryRoute } from '@/backoffice/app/routes/error-boundary/error-boundary';
import { featureFlagCatalogRoutes } from '@/backoffice/app/routes/feature-flag-catalog/routes';
import { groupsRoutes } from '@/backoffice/app/routes/groups/routes';
import { ideaasRoutes } from '@/backoffice/app/routes/ideaas/routes';
import { rolesRoutes } from '@/backoffice/app/routes/roles/routes';
import { AppRoot, rootLoader } from '@/backoffice/app/routes/root/root';
import { sreHubRoutes } from '@/backoffice/app/routes/sre-hub/routes';
import { tenantRoutes } from '@/backoffice/app/routes/tenants/routes';
import { userProfileRoutes } from '@/backoffice/app/routes/user-profile/routes';
import { userRoutes } from '@/backoffice/app/routes/users/routes';
import { BackofficePermissionsProvider } from '@/backoffice/features/permissions/backoffice-permissions-provider';
import { authRoutes } from '@/shared/app/auth/routes';
import { FullscreenLoader } from '@/shared/components/ui/fullscreen-loader/fullscreen-loader';
import { AppConfigProvider } from '@/shared/features/app-config/context/app-config-context';
import { useLogRocket } from '@/shared/hooks/use-log-rocket/use-log-rocket';
import { TCustomRouteObject } from '@/shared/types/router.types';

const errorElement = (
  <AppConfigProvider value={backofficeAppConfig}>
    <BackofficeErrorBoundaryRoute />
  </AppConfigProvider>
);

const route: TCustomRouteObject[] = [
  authRoutes,
  {
    path: '/',
    element: (
      <AppConfigProvider value={backofficeAppConfig}>
        <BackofficePermissionsProvider>
          <ProtectedRoute>
            <AppRoot />
          </ProtectedRoute>
        </BackofficePermissionsProvider>
      </AppConfigProvider>
    ),
    loader: rootLoader,
    shouldRevalidate: () => false,
    hydrateFallbackElement: <FullscreenLoader />,
    errorElement,
    children: [
      tenantRoutes,
      groupsRoutes,
      dataExportRoutes,
      ec2OrchestratorConfigRoutes,
      ideaasRoutes,
      sreHubRoutes,
      legacyConsoleLatencyRoutes,
      rolesRoutes,
      userRoutes,
      userProfileRoutes,
      featureFlagCatalogRoutes,
    ],
  },
  {
    path: '/404',
    element: errorElement,
  },
  {
    path: '*',
    element: (
      <ProtectedRoute>
        <Navigate to="/" replace />
      </ProtectedRoute>
    ),
  },
];

export const createAppRouter = () => createBrowserRouter(route);

export const AppRouter = () => {
  const [router] = useState(createAppRouter);
  useLogRocket();

  return <RouterProvider router={router} />;
};
